import React from 'react'
import styled, { keyframes } from 'styled-components'
import { DSButton, DSCheckbox, DSSpacer, DSText } from '@zoe/ds-web'
import { onBackClickCallback, onNextClickType } from 'components/types'
import { ContextAwareText } from '../core/text/ContextAwareText'
import { Quizzes } from '../../redux/quiz/types'
import { GifName, resolveTopImage } from '../../data/gifs'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'

export type MandatoryConsentScreenProps = {
  title: string
  description?: string
  topImage?: string
  topImageAlt?: string
  topImageAspectRatio?: string
  gif?: GifName
  consentQuestionKey: string
  buttonText?: string
  // Ticked points under the description (e12). Supports <b> and quiz tokens,
  // e.g. {privacyPolicy}.
  points?: string[]
  // e12: Heading 700 instead of 600 for the title.
  largeTitle?: boolean
} & ({ hideCheckbox?: false; consentLabel: string } | { hideCheckbox: true; consentLabel?: never })

type Props = MandatoryConsentScreenProps & {
  onNextClick: onNextClickType
  onBackClick: onBackClickCallback
  quiz: Quizzes
}

const imgFadeIn = keyframes`
  from { opacity: 0; transform: scale(0.97); }
  to { opacity: 1; transform: scale(1); }
`

const TopImage = styled.img.attrs({ loading: 'lazy' })`
  display: block;
  width: 100%;
  object-fit: cover;
  border-radius: var(--radius-12);
  animation: ${imgFadeIn} 0.3s ease-out both;
`

const ConsentBox = styled.label`
  display: flex;
  align-items: center;
  gap: var(--grid-12);
  padding: var(--grid-16) var(--grid-24);
  background-color: var(--colour-surface-cream-subtle);
  border-radius: var(--radius-8);
  cursor: pointer;
`

const Points = styled.ul`
  display: flex;
  flex-direction: column;
  gap: var(--grid-12);
  margin: 0;
  padding: 0;
  list-style: none;
`

const Point = styled.li`
  display: flex;
  align-items: flex-start;
  gap: var(--grid-8);
`

const Tick = styled.img`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`

// Opens in a new tab so the quiz isn't lost. Regular weight, unlike the
// shared {privacyPolicy} token: && outranks DSText's own semibold `a` rule.
const PrivacyLink = styled.a.attrs({
  href: 'https://zoe.com/privacy-policy',
  target: '_blank',
  rel: 'noopener noreferrer',
})`
  && {
    color: var(--colour-text-link);
    font-family: 'LotaGrotesqueAlt1-Regular', sans-serif;
    font-weight: normal;
  }
`

const POINT_TOKENS = { privacyPolicy: <PrivacyLink>Privacy Policy</PrivacyLink> }

const ConsentText = styled.div`
  flex: 1;
  min-width: 0;
`

export const MandatoryConsentScreen: React.FC<Props> = (props) => {
  const {
    title,
    description,
    topImage,
    topImageAlt,
    topImageAspectRatio,
    consentLabel,
    hideCheckbox,
    consentQuestionKey,
    buttonText,
    points,
    largeTitle,
    onNextClick,
    quiz,
  } = props

  const image = resolveTopImage(props.gif, { topImage, topImageAlt, topImageAspectRatio })
  const [isAccepted, setIsAccepted] = React.useState(false)
  const canContinue = hideCheckbox || isAccepted

  const imageElement = image.topImage && (
    <TopImage
      src={image.topImage}
      alt={image.topImageAlt ?? ''}
      style={image.topImageAspectRatio ? { aspectRatio: image.topImageAspectRatio } : undefined}
    />
  )

  const copy = (
    <>
      <DSText variant={largeTitle ? 'fluid-heading-700' : 'fluid-heading-600'} weight="semiBold" align="left">
        <ContextAwareText text={title} quiz={quiz} />
      </DSText>

      {description && (
        <>
          {/* With points (e12) the copy reads as one block: primary text, spaced like the list. */}
          <DSSpacer size={points ? 16 : 8} direction="vertical" />
          <DSText variant="fluid-paragraph-300" color={points ? 'primary' : 'secondary'} align="left">
            <ContextAwareText text={description} quiz={quiz} />
          </DSText>
        </>
      )}

      {points && points.length > 0 && (
        <>
          <DSSpacer size={16} direction="vertical" />
          <Points>
            {points.map((point) => (
              <Point key={point}>
                <Tick src="/img/shared/tick-single.svg" alt="" />
                <DSText variant="fluid-paragraph-300" align="left">
                  <ContextAwareText text={point} quiz={quiz} additionalTokens={POINT_TOKENS} />
                </DSText>
              </Point>
            ))}
          </Points>
        </>
      )}

      <DSSpacer size={144} direction="vertical" />
    </>
  )

  const footer = (
    <StickyFooter $expanded={!hideCheckbox}>
      <StickyFooterContent>
        {!hideCheckbox && (
          <>
            <ConsentBox>
              <ConsentText>
                <DSText variant="fluid-label-100" color="secondary">
                  <ContextAwareText text={consentLabel} quiz={quiz} />
                </DSText>
              </ConsentText>
              <DSCheckbox
                size="small"
                isSelected={isAccepted}
                onChange={setIsAccepted}
                testId="mandatory-consent-checkbox"
              />
            </ConsentBox>
            <DSSpacer size={8} direction="vertical" />
          </>
        )}
        <DSButton
          variant="primary"
          colorScheme="default"
          size="large"
          width="full"
          isDisabled={!canContinue}
          onPress={() => onNextClick({ [consentQuestionKey]: true })}
          testId="continue-button"
        >
          <DSText variant="fluid-paragraph-200" weight="semiBold">
            {buttonText ?? 'Continue'}
          </DSText>
        </DSButton>
      </StickyFooterContent>
    </StickyFooter>
  )

  return (
    <ScreenContainer>
      {imageElement}
      <DSSpacer size={24} direction="vertical" />
      {copy}
      {footer}
    </ScreenContainer>
  )
}
